/**
 * InfoCard — DGUI component for key–value summary cards.
 * Presentational only; schema-driven from agent/backend.
 */

import { Card, CardContent, Typography, Box } from '@mui/material';
import type { InfoCardProps } from '@flowrithm/shared/src/features/ai-assist/types/dgui';

const variantSpacing = {
  default: { py: 1.5, px: 2 },
  compact: { py: 1, px: 1.5 },
} as const;

export function InfoCard({
  title,
  subtitle,
  items,
  variant = 'default',
}: InfoCardProps) {
  const spacing = variantSpacing[variant] ?? variantSpacing.default;
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'rgba(4, 55, 123, 0.2)',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        maxWidth: 360,
        mt: 1,
        mb: 1,
      }}
    >
      <CardContent sx={{ ...spacing, '&:last-child': { pb: spacing.py } }}>
        {title && (
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="#04377b"
            gutterBottom
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        <Box component="dl" sx={{ m: 0 }}>
          {safeItems.map((item, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                py: variant === 'compact' ? 0.25 : 0.5,
                borderBottom:
                  i < safeItems.length - 1
                    ? '1px solid rgba(0,0,0,0.06)'
                    : 'none',
              }}
            >
              <Typography component="dt" variant="body2" color="text.secondary">
                {item.label}
              </Typography>
              <Typography component="dd" variant="body2" fontWeight={500} sx={{ textAlign: 'right' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
